import { Component, inject } from '@angular/core';
import { MJNotificationService } from '@memberjunction/ng-notifications';
import { MJConfirmService } from '@memberjunction/ng-ui-components';
import type { MJUserRoleEntity } from '@memberjunction/core-entities';
import { OEP_ENTITY, OEP_PRESENTER_ROLE_ID, type OEPFileKind } from 'agu_oep_core';
import type { FeedbackPlatformPresentationEntity } from 'mj_generatedentities';
import { OEPDataService } from '../../data/oep-data.service';
import { OEPFilesService } from '../../data/oep-files.service';
import { OEPViewBase } from '../../state/oep-view-base';
import {
    DefaultRole, FilterPeople, OEP_ACTIVITY_ROLES, OEP_PARTICIPANT_ACTIVITY_QUERY_ID, ParsePersonRow,
    PersonRows, RoleTabs, ShowOptions,
    type OEPActivityPerson, type OEPActivityPersonVM, type OEPActivityRole, type OEPActivityShow,
    type OEPActivityShowOption, type OEPActivityTab,
} from '../activity/activity.vm';
import { OEPPresentationLinkSql, OEPUploadedFilesSql } from '../shared/files';
import { ClockET } from '../shared/format';
import { OEP_TAB_BLOCKED, OEPOpenInNewTab } from '../shared/open-tab';
import { Str, Up, type OEPRow } from '../shared/rows';
import type { OEPStatusLine } from '../shared/status';
import {
    BuildPeoplePage, BuildUserManagementRecord, MergeCanceledPresenters, OEP_NOT_UPLOADED, OEP_NO_PRESENTER,
    ParseSections, PickPresenterPresentation, PlanUserManagementSave,
    type OEPSectionOption, type OEPUploadedFileVM, type OEPUserRecordVM, type OEPUserSavePlan,
} from './user-management.vm';

/**
 * AGU staff "User management" view — paginated directory of season participants with `<mj-page-search>`
 * substring search, role tabs, Show filters, and an inline presenter record editor
 * (Name, Section, Presentation name, Draft/Final storage paths + signed download links, Active/Inactive status).
 */
@Component({
    standalone: false,
    selector: 'oep-user-management',
    templateUrl: './oep-user-management.component.html',
    styleUrl: './oep-user-management.component.scss',
})
export class OEPUserManagementComponent extends OEPViewBase {
    private readonly files = inject(OEPFilesService);
    private readonly confirm = inject(MJConfirmService);

    SeasonCode = '';
    AsOf = '';
    Tabs: OEPActivityTab[] = [];
    Role: OEPActivityRole = 'Presenter';
    ShowChoices: OEPActivityShowOption[] = ShowOptions('Presenter');
    Show: OEPActivityShow = 'all';
    Search = '';
    Shown: OEPActivityPersonVM[] = [];
    Page = 1;
    TotalPages = 1;
    CountLine = '';

    Sections: OEPSectionOption[] = [];
    Status: OEPStatusLine | null = null;

    Record: OEPUserRecordVM | null = null;
    SelectedActivity: OEPActivityPersonVM | null = null;
    DraftSectionID = '';
    DraftTitle = '';
    DraftIsActive = true;
    BlockedUrl: Partial<Record<OEPFileKind, string>> = {};

    readonly NotUploaded = OEP_NOT_UPLOADED;

    private people: OEPActivityPerson[] = [];
    private matches: OEPActivityPerson[] = [];
    private roleChosen = false;

    get HasChanges(): boolean {
        if (!this.Record) return false;
        const plan = PlanUserManagementSave(this.Record, {
            SectionID: this.DraftSectionID,
            Title: this.DraftTitle,
            IsActive: this.DraftIsActive,
        });
        return !plan.Ok || plan.HasChanges;
    }

    protected async Load(): Promise<void> {
        const season = this.Context.Season;
        if (!season) return;
        const sid = OEPDataService.G(season.ID);
        const [[activityRows], [sections, canceledPresentations]] = await Promise.all([
            this.Data.Queries([OEP_PARTICIPANT_ACTIVITY_QUERY_ID]),
            this.Data.Batch([
                { EntityName: OEP_ENTITY.Sections, ExtraFilter: '1=1', Fields: ['ID', 'Code', 'Name'], OrderBy: 'Name' },
                {
                    EntityName: OEP_ENTITY.Presentations,
                    ExtraFilter: `SeasonID = '${sid}' AND Status = 'Canceled'`,
                    Fields: ['ID', 'PresenterUserID', 'SectionID', 'PresentationCode', 'Title', 'Format', 'Status', '__mj_CreatedAt'],
                    OrderBy: '__mj_CreatedAt, ID',
                },
            ]),
        ]);
        const canceledUsers = await this.fetchCanceledUsers(canceledPresentations);
        this.SeasonCode = season.Code;
        this.AsOf = `Updated ${ClockET(new Date())}`;
        this.Sections = ParseSections(sections);
        this.people = MergeCanceledPresenters(activityRows.map(ParsePersonRow), canceledPresentations, canceledUsers, this.Sections);
        this.Tabs = RoleTabs(this.people);
        if (!this.roleChosen) this.Role = DefaultRole(this.people);
        this.rebuildRole(false);
        this.syncSelectedActivity();
    }

    SelectRole(key: string): void {
        const role = OEP_ACTIVITY_ROLES.find((r) => r === key);
        if (!role || role === this.Role) return;
        this.Role = role;
        this.roleChosen = true;
        if (role !== 'Presenter') {
            this.Record = null;
            this.SelectedActivity = null;
        }
        this.rebuildRole(true);
        this.Changed();
    }

    SetShow(value: string): void {
        const choice = this.ShowChoices.find((o) => o.Value === value);
        if (!choice) return;
        this.Show = choice.Value;
        this.rebuildPeople(true);
        this.Changed();
    }

    SetSearch(value: string): void {
        this.Search = value;
        this.rebuildPeople(true);
        this.Changed();
    }

    PrevPage(): void {
        if (this.Page <= 1) return;
        this.Page--;
        this.rebuildPeople(false);
        this.Changed();
    }

    NextPage(): void {
        if (this.Page >= this.TotalPages) return;
        this.Page++;
        this.rebuildPeople(false);
        this.Changed();
    }

    IsSelected(userID: string): boolean {
        return Boolean(this.Record && Up(this.Record.UserID) === Up(userID));
    }

    CloseRecord(): void {
        this.Record = null;
        this.SelectedActivity = null;
        this.Status = null;
        this.BlockedUrl = {};
        this.Changed();
    }

    OnDraftChange(): void {
        this.Changed();
    }

    async SelectPerson(userID: string): Promise<void> {
        if (this.Busy || this.Role !== 'Presenter') return;
        this.Status = null;
        this.BlockedUrl = {};
        await this.guard(async () => {
            const loaded = await this.fetchRecordByUserID(userID);
            if (!loaded) {
                this.Record = null;
                this.Status = { Text: OEP_NO_PRESENTER, Tone: 'error' };
                return;
            }
            this.applyRecord(loaded);
        });
    }

    async SaveChanges(): Promise<void> {
        if (this.Busy || !this.Record) return;
        this.Status = null;
        const rec = this.Record;
        const plan = PlanUserManagementSave(rec, {
            SectionID: this.DraftSectionID,
            Title: this.DraftTitle,
            IsActive: this.DraftIsActive,
        });
        if (!plan.Ok) {
            this.Status = { Text: plan.Error, Tone: 'error' };
            this.Changed();
            return;
        }
        if (!plan.HasChanges) return;
        if (plan.ConfirmInactivate && !(await this.confirmInactivation(rec.Name))) return;

        await this.guard(async () => {
            await this.executeSavePlan(rec, plan);
            await this.Context.Reload();
            await this.Load();
            const message = `Saved changes for ${rec.Name}.`;
            this.Status = { Text: message, Tone: 'ok' };
            MJNotificationService.Instance.CreateSimpleNotification(message, 'success', 5000);
        });
    }

    async OpenFile(kind: OEPFileKind, file: OEPUploadedFileVM): Promise<void> {
        if (this.Busy) return;
        this.Status = null;
        this.BlockedUrl[kind] = undefined;
        await this.guard(async () => {
            const opened = await OEPOpenInNewTab(() => this.files.DownloadUrl(file.FileID));
            if (opened.Blocked) {
                this.BlockedUrl[kind] = opened.Url;
                this.Status = { Text: OEP_TAB_BLOCKED, Tone: 'error' };
            }
        });
    }

    private rebuildRole(resetPaging: boolean): void {
        this.ShowChoices = ShowOptions(this.Role);
        if (!this.ShowChoices.some((o) => o.Value === this.Show)) this.Show = 'all';
        this.rebuildPeople(resetPaging);
    }

    private rebuildPeople(resetPaging: boolean): void {
        if (resetPaging) this.Page = 1;
        const inRole = this.people.filter((p) => p.Role === this.Role);
        this.matches = FilterPeople(inRole, this.Show, this.Search);
        const page = BuildPeoplePage(inRole, this.matches, this.Role, this.Page);
        this.Shown = page.Rows;
        this.Page = page.Page;
        this.TotalPages = page.TotalPages;
        this.CountLine = page.CountLine;
    }

    private applyRecord(rec: OEPUserRecordVM): void {
        this.Record = rec;
        this.DraftSectionID = rec.SectionID;
        this.DraftTitle = rec.Title;
        this.DraftIsActive = rec.IsActive;
        this.syncSelectedActivity();
    }

    private syncSelectedActivity(): void {
        if (!this.Record) {
            this.SelectedActivity = null;
            return;
        }
        const uid = Up(this.Record.UserID);
        const match = this.people.find((p) => p.Role === 'Presenter' && Up(p.UserID) === uid);
        this.SelectedActivity = match ? PersonRows([match])[0] : null;
    }

    private confirmInactivation(presenterName: string): Promise<boolean> {
        return this.confirm.Confirm({
            title: 'Inactivate presenter',
            message: `Inactivate ${presenterName}?`,
            detail: 'Their OEP Presenter role will be revoked so they cannot sign into OEP, and their presentation will be marked Canceled. You can re-activate them at any time.',
            confirmText: 'Inactivate presenter',
            cancelText: 'Cancel',
            type: 'warning',
        });
    }

    private async executeSavePlan(rec: OEPUserRecordVM, plan: Extract<OEPUserSavePlan, { readonly Ok: true }>): Promise<void> {
        try {
            if (plan.UpdatePresentation) {
                const next = plan.UpdatePresentation;
                await this.Data.Update<FeedbackPlatformPresentationEntity>(OEP_ENTITY.Presentations, rec.PresentationID, (p) => {
                    p.SectionID = next.SectionID;
                    p.Title = next.Title;
                    p.Status = next.Status as FeedbackPlatformPresentationEntity['Status'];
                });
            }
            for (const roleRowID of plan.RevokePresenterRoleRowIDs) {
                await this.Data.Delete('MJ: User Roles', roleRowID);
            }
            if (plan.GrantPresenterRole) {
                await this.Data.SaveNew<MJUserRoleEntity>('MJ: User Roles', (ur) => {
                    ur.UserID = rec.UserID;
                    ur.RoleID = OEP_PRESENTER_ROLE_ID;
                });
            }
        } finally {
            try {
                const fresh = await this.fetchRecordByUserID(rec.UserID);
                if (fresh) this.applyRecord(fresh);
            } catch {
                // Preserve any primary mutation error if post-save refresh fails.
            }
        }
    }

    private async fetchCanceledUsers(canceledPresentations: readonly OEPRow[]): Promise<OEPRow[]> {
        const ids = [...new Set(canceledPresentations.map((p) => Str(p, 'PresenterUserID')).filter(Boolean))]
            .map((id) => `'${OEPDataService.G(id)}'`);
        if (ids.length === 0) return [];
        const [users] = await this.Data.Batch([
            { EntityName: 'MJ: Users', ExtraFilter: `ID IN (${ids.join(',')})`, Fields: ['ID', 'Name', 'FirstName', 'LastName', 'Email'] },
        ]);
        return users;
    }

    private async fetchRecordByUserID(userID: string): Promise<OEPUserRecordVM | null> {
        const season = this.Context.Season;
        if (!season) return null;
        const sid = OEPDataService.G(season.ID);
        const uid = OEPDataService.G(userID);
        const [users, userRoles, presentations] = await this.Data.Batch([
            { EntityName: 'MJ: Users', ExtraFilter: `ID = '${uid}'`, Fields: ['ID', 'Name', 'FirstName', 'LastName', 'Email'] },
            { EntityName: 'MJ: User Roles', ExtraFilter: `UserID = '${uid}' AND Role = 'OEP Presenter'`, Fields: ['ID', 'UserID', 'RoleID', 'Role'] },
            {
                EntityName: OEP_ENTITY.Presentations,
                ExtraFilter: `SeasonID = '${sid}' AND Status <> 'Archived' AND PresenterUserID = '${uid}'`,
                Fields: ['ID', 'PresenterUserID', 'SectionID', 'Title', 'Status', '__mj_CreatedAt'],
                OrderBy: '__mj_CreatedAt, ID',
            },
        ]);
        const chosen = PickPresenterPresentation(presentations);
        if (users.length !== 1 || !chosen) return null;

        const [files, links] = await this.fetchPresentationFiles(Str(chosen, 'ID'));
        return BuildUserManagementRecord({ Users: users, UserRoles: userRoles, Presentations: presentations, Files: files, Links: links });
    }

    private fetchPresentationFiles(presentationID: string): Promise<Record<string, unknown>[][]> {
        const pidSql = `'${OEPDataService.G(presentationID)}'`;
        return this.Data.Batch([
            {
                EntityName: OEP_ENTITY.Files,
                ExtraFilter: OEPUploadedFilesSql(pidSql, ['Draft', 'Final']),
                Fields: ['ID', 'Name', 'Category', 'Status', 'ProviderKey', '__mj_CreatedAt'],
                OrderBy: '__mj_CreatedAt DESC',
            },
            {
                EntityName: OEP_ENTITY.FileEntityRecordLinks,
                ExtraFilter: OEPPresentationLinkSql(pidSql),
                Fields: ['FileID', 'RecordID'],
            },
        ]);
    }

    private async guard(work: () => Promise<void>): Promise<void> {
        this.Busy = true;
        this.Changed();
        try {
            await work();
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            this.Status = { Text: message, Tone: 'error' };
            MJNotificationService.Instance.CreateSimpleNotification(message, 'error', 5000);
        } finally {
            this.Busy = false;
            this.Changed();
        }
    }
}
